<script setup lang="ts">
import { ref } from 'vue';
type Mapping = {
  apartment: string;
  propertyId: number;
  roomId: number;
  unitId: number;
  timeZone: string;
};
type Candidate = { id: number; arrival: string; departure: string; fingerprint: string };
type Order = {
  id: string;
  apartment: string;
  deliveryDeadlineAt: string;
  assignedBoxId: string | null;
  reservation: { bookingId: number; reviewRequired: boolean } | null;
  candidates: Candidate[];
};
type Status = {
  configured: boolean;
  mappings: Mapping[];
  apartments: string[];
  bookingCount: number;
  lastSync: { at: string; count: number } | null;
  orders: Order[];
};
const data = ref<Status | null>(null),
  busy = ref(false),
  error = ref(''),
  message = ref('');
const mappings = ref<Mapping[]>([]);
const start = ref(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
const end = ref(new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10));
const selected = ref<Record<string, number>>({});
const errors: Record<string, string> = {
  BEDS24_NOT_CONFIGURED: 'Accès Beds24 à configurer côté serveur. Aucun secret à saisir ici.',
  BEDS24_READ_SCOPE_REQUIRED:
    'Le jeton doit autoriser la lecture des réservations sans droit d’écriture.',
  BEDS24_ACCESS_DENIED: 'Beds24 refuse cet accès. Vérifiez le jeton et son périmètre.',
  BEDS24_RATE_LIMITED: 'Quota Beds24 atteint. Réessayez plus tard.',
  BEDS24_UNAVAILABLE: 'Beds24 est indisponible. Les données précédentes sont conservées.',
  BEDS24_RESPONSE_INVALID: 'Réponse Beds24 non exploitable. Aucune mise à jour enregistrée.',
  BEDS24_LIMIT_EXCEEDED: 'Volume trop important pour une synchronisation. Réduisez la période.',
  BEDS24_MAPPING_REQUIRED: 'Enregistrez au moins une correspondance de logement.',
  BEDS24_MAPPING_DUPLICATE:
    'Chaque unité et chaque logement ne peuvent être associés qu’une fois.',
  BEDS24_APARTMENT_UNKNOWN: 'Configurez d’abord ce logement dans OTEM.',
  BEDS24_LINK_CONFLICT: 'Le séjour ou la commande a changé. Actualisez avant de confirmer.',
  BEDS24_SYNC_CONFLICT: 'Les correspondances ont changé. Relancez la synchronisation.',
  BEDS24_SYNC_BUSY: 'Une synchronisation est déjà en cours.',
  BEDS24_INPUT_INVALID: 'Vérifiez les identifiants, le fuseau et les dates (un an maximum).',
};
async function request(path = '', method = 'GET', body?: unknown) {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  message.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/beds24' + path, {
      method,
      credentials: 'include',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const result = await response.json();
    if (!response.ok)
      throw new Error(
        errors[result.error] ?? 'Opération impossible. Réessayez après actualisation.',
      );
    data.value = result;
    mappings.value = globalThis.structuredClone(result.mappings);
    selected.value = {};
    if (method !== 'GET')
      message.value = path === '/sync' ? 'Synchronisation terminée.' : 'Enregistré.';
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Connexion impossible.';
  } finally {
    busy.value = false;
  }
}
function open(event: { target: unknown }) {
  if ((event.target as { open: boolean }).open && !data.value) void request();
}
function add() {
  mappings.value.push({
    apartment: data.value?.apartments[0] ?? '',
    propertyId: 0,
    roomId: 0,
    unitId: 1,
    timeZone: 'Europe/Paris',
  });
}
function link(order: Order) {
  const candidate = order.candidates.find((c) => c.id === selected.value[order.id]);
  if (candidate)
    void request('/orders/' + encodeURIComponent(order.id), 'PUT', {
      bookingId: candidate.id,
      fingerprint: candidate.fingerprint,
    });
}
</script>
<template>
  <details class="beds-panel" @toggle="open">
    <summary>Réservations Beds24</summary>
    <p>Associez les séjours aux commandes. La Box reste affectée lors de la préparation.</p>
    <p v-if="error" role="alert">{{ error }}</p>
    <p v-if="message" role="status">{{ message }}</p>
    <button type="button" :disabled="busy" @click="request()">Actualiser</button>
    <div v-if="data">
      <p>
        {{
          data.configured
            ? 'Accès configuré — connexion à vérifier par synchronisation.'
            : 'Beds24 non connecté.'
        }}
      </p>
      <p v-if="data.lastSync">
        Dernière lecture : {{ new Date(data.lastSync.at).toLocaleString('fr-FR') }} ·
        {{ data.lastSync.count }} séjours lus.
      </p>
      <p v-else>Aucune lecture validée pour les correspondances actuelles.</p>
      <h2>Correspondances des logements</h2>
      <p>
        Renseignez les identifiants Beds24 de l’établissement, du type de logement et de l’unité
        physique.
      </p>
      <form @submit.prevent="request('/mappings', 'PUT', { mappings })">
        <fieldset v-for="(m, index) in mappings" :key="index" :disabled="busy">
          <legend>Logement {{ index + 1 }}</legend>
          <label
            >Logement OTEM<select v-model="m.apartment" required>
              <option v-for="apartment in data.apartments" :key="apartment" :value="apartment">
                {{ apartment }}
              </option>
            </select></label
          >
          <label
            >Établissement Beds24<input
              v-model.number="m.propertyId"
              type="number"
              min="1"
              step="1"
              required
          /></label>
          <label
            >Type de logement Beds24<input
              v-model.number="m.roomId"
              type="number"
              min="1"
              step="1"
              required
          /></label>
          <label
            >Unité Beds24<input
              v-model.number="m.unitId"
              type="number"
              min="1"
              step="1"
              required
          /></label>
          <label>Fuseau horaire<input v-model="m.timeZone" required /></label>
          <button type="button" @click="mappings.splice(index, 1)">
            Retirer la correspondance
          </button>
        </fieldset>
        <button type="button" :disabled="busy || !data.apartments.length" @click="add">
          Ajouter un logement
        </button>
        <button type="submit" :disabled="busy">Enregistrer les correspondances</button>
      </form>
      <h2>Lire les réservations</h2>
      <p>
        Choisissez la période d’arrivée. Les séjours déjà liés sont revérifiés même s’ils ont
        changé de date. Aucun paiement n’est modifié.
      </p>
      <form @submit.prevent="request('/sync', 'POST', { arrivalFrom: start, arrivalTo: end })">
        <label>Arrivées à partir du<input v-model="start" type="date" required /></label>
        <label>Arrivées jusqu’au<input v-model="end" type="date" required /></label>
        <button type="submit" :disabled="busy || !data.configured || !data.mappings.length">
          {{ busy ? 'Lecture en cours…' : 'Synchroniser Beds24' }}
        </button>
      </form>
      <h2>Rattacher les commandes</h2>
      <p>
        Vérifiez le séjour dans Beds24 avant de confirmer. Le logement et la date ne prouvent
        pas l’identité du voyageur. Les observations expirent après 24 h.
      </p>
      <p v-if="!data.orders.length">Aucune commande à rattacher.</p>
      <article v-for="order in data.orders" :key="order.id">
        <h3>{{ order.apartment }}</h3>
        <p>Commande {{ order.id }}</p>
        <p>Livraison : {{ new Date(order.deliveryDeadlineAt).toLocaleString('fr-FR') }}</p>
        <p v-if="order.assignedBoxId">Box : {{ order.assignedBoxId }}</p>
        <p
          v-if="order.reservation"
          :class="order.reservation.reviewRequired ? 'review' : 'confirmed'"
        >
          Séjour {{ order.reservation.bookingId }} ·
          {{ order.reservation.reviewRequired ? 'À vérifier' : 'Lien confirmé' }}
        </p>
        <form @submit.prevent="link(order)">
          <label
            >Séjour correspondant<select v-model="selected[order.id]" :disabled="busy" required>
              <option :value="undefined" disabled>Sélectionner un séjour</option>
              <option v-for="c in order.candidates" :key="c.id" :value="c.id">
                #{{ c.id }} · {{ c.arrival }} → {{ c.departure }}
              </option>
            </select></label
          >
          <p v-if="!order.candidates.length">
            Aucun séjour confirmé et récemment vérifié pour ce logement et cette date.
            Synchronisez ou contrôlez la correspondance.
          </p>
          <button type="submit" :disabled="busy || !selected[order.id]">
            Confirmer le rattachement
          </button>
          <button
            v-if="order.reservation"
            type="button"
            :disabled="busy"
            @click="request('/orders/' + encodeURIComponent(order.id), 'DELETE')"
          >
            Retirer le lien
          </button>
        </form>
      </article>
    </div>
  </details>
</template>
<style scoped>
.beds-panel {
  background: #fff;
  border: 1px solid #bdc9db;
  border-radius: 16px;
  padding: 1rem;
  min-width: 0;
  overflow-wrap: anywhere;
}
summary {
  font-weight: 750;
  cursor: pointer;
  min-height: 44px;
}
h2 {
  font-size: 1.1rem;
  margin-top: 1.5rem;
}
form,
fieldset,
label {
  display: grid;
  gap: 0.6rem;
  min-width: 0;
}
form {
  margin-block: 1rem;
}
fieldset,
article {
  border: 1px solid #c8d2df;
  border-radius: 12px;
  padding: 0.8rem;
  margin-block: 0.8rem;
}
input,
select,
button {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  font: inherit;
  border-radius: 8px;
  padding: 0.6rem;
}
input,
select {
  border: 1px solid #8192a9;
  background: white;
  color: #17263b;
}
button {
  background: #153889;
  color: white;
  border: 0;
  cursor: pointer;
}
button:disabled {
  opacity: 0.55;
  cursor: default;
}
[role='alert'],
.review {
  color: #842012;
  background: #fff1cc;
  padding: 0.7rem;
  border-radius: 8px;
}
.confirmed {
  color: #17603f;
  font-weight: 700;
}
</style>
